import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit3, X, Save, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import api, { resolveUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ImageUploader from '../../components/admin/ImageUploader';

const EMPTY = { name: '', image: '' };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { toast } = useToast();

  const load = () => api.get('/admin/categories').then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const startNew = () => { setEditing('new'); setForm(EMPTY); };
  const startEdit = (c) => { setEditing(c.id); setForm({ name: c.name, image: c.image || '' }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/admin/categories', { name: form.name, image: form.image, order: cats.length });
      else await api.put(`/admin/categories/${editing}`, { ...form, order: cats.find((c) => c.id === editing)?.order || 0 });
      toast({ title: 'Saved', description: form.name });
      cancel(); load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Save failed' });
    }
  };

  const del = async (c) => {
    if (c.itemCount > 0) {
      toast({ title: 'Cannot delete', description: `${c.itemCount} item(s) are still in this category. Move or delete them first.` });
      return;
    }
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Delete failed' });
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= cats.length) return;
    const next = [...cats];
    [next[idx], next[target]] = [next[target], next[idx]];
    setCats(next);
    try {
      await api.post('/admin/categories/reorder', { orderedIds: next.map((c) => c.id) });
    } catch (err) {
      toast({ title: 'Error', description: 'Reorder failed' });
      load();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#0F1C2E]">Categories</h1>
          <p className="text-gray-500 text-sm mt-1">Reorder here to change how sections appear on your public menu.</p>
        </div>
        <button onClick={startNew} className="btn-wrappus text-sm shrink-0"><Plus size={16} /> Add Category</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-2xl p-6 shadow-sm mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-lg">{editing === 'new' ? 'New Category' : 'Rename / Edit Category'}</h2>
            <button type="button" onClick={cancel} className="p-2 hover:bg-gray-100 rounded"><X size={18} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Category Name *"><input required value={form.name} onChange={on('name')} className="inp" placeholder="e.g. Signature Donairs" /></F>
            <F label="Category Thumbnail (optional)">
              <ImageUploader value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label="" hint="Small square image shown next to the section title on the public menu." />
            </F>
          </div>
          {editing !== 'new' && <p className="text-xs text-gray-500">Renaming will automatically update all items in this category.</p>}
          <button type="submit" className="btn-wrappus text-sm"><Save size={16} /> Save</button>
          <style>{`.inp{width:100%;border-radius:0.75rem;border:1px solid #E5E7EB;background:white;padding:0.65rem 0.9rem;font-size:0.9rem;} .inp:focus{outline:none;border-color:#ED2E2E;box-shadow:0 0 0 3px rgba(237,46,46,0.15);} `}</style>
        </form>
      )}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {cats.length === 0 ? <div className="p-8 text-center text-gray-400 text-sm">No categories yet</div> : cats.map((c, idx) => (
          <div key={c.id} className="border-b border-gray-50 last:border-b-0 flex items-center gap-3 px-4 py-3">
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-[#ED2E2E] disabled:opacity-30 disabled:cursor-not-allowed"><ChevronUp size={14} /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === cats.length - 1} className="p-1 text-gray-400 hover:text-[#ED2E2E] disabled:opacity-30 disabled:cursor-not-allowed"><ChevronDown size={14} /></button>
            </div>
            <GripVertical size={14} className="text-gray-300" />
            {c.image && <img src={resolveUrl(c.image)} alt="" className="w-10 h-10 rounded-lg object-cover" />}
            <div className="flex-1">
              <div className="font-heading font-semibold text-[#0F1C2E]">{c.name}</div>
              <div className="text-xs text-gray-500">{c.itemCount} item{c.itemCount !== 1 ? 's' : ''}</div>
            </div>
            <a href={`/admin/menu?category=${encodeURIComponent(c.name)}`} className="text-xs text-[#ED2E2E] font-semibold hover:underline">View items →</a>
            <button onClick={() => startEdit(c)} className="p-2 hover:bg-gray-100 rounded text-gray-500 hover:text-[#ED2E2E]"><Edit3 size={16} /></button>
            <button onClick={() => del(c)} className="p-2 hover:bg-red-50 rounded text-gray-500 hover:text-red-600"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="text-xs font-semibold text-gray-600 uppercase block mb-1">{label}</label>{children}</div>;
}
